/* eslint-disable @typescript-eslint/no-non-null-assertion */
import debounce                     from 'lodash.debounce';
import { observable }                    from 'rewire-core';
import { ICell, IRow }                   from 'rewire-grid';
import { ITimeOffRequest }               from '../DataTypes';
import { areSetsEqual }                  from '../utility/Utils';
import timeOffModel, { TimeOffCellType } from './TimeOffModel';
import infoPanelTimeOffRequestViewModel  from './InfoPanelTimeOffRequestViewModel';

interface ITimeOffInfoPanelModelState {
  loadValue: any;
  isLoading: boolean;
}

export class TimeOffInfoPanelModel {
  state: ITimeOffInfoPanelModelState;
  private _active: boolean;

  constructor() {
    this.load = debounce(this.load, 250, { leading: true, trailing: true }) as any;

    this.state = observable({
      loadValue: undefined,
      isLoading: false,
    });

    this._active = false;
  }

  activate() {
    if (!this._active) {
      this._active = true;
      infoPanelTimeOffRequestViewModel.onEvent.addListener('submit', this.submit);
    }
  }

  deactivate() {
    if (this._active) {
      this._active = false;
      infoPanelTimeOffRequestViewModel.onEvent.removeListener('submit', this.submit);
    }
  }

  get selectedCells(): ICell[] {
    return timeOffModel.selectedCells;
  }

  get selectedCell(): ICell | undefined {
    return timeOffModel.selectedCell;
  }

  get selectedCellType(): TimeOffCellType | undefined {
    if (!this.selectedCell) return;

    return timeOffModel.getCellType(this.selectedCell);
  }

  get selectedCellsType(): TimeOffCellType | undefined {
    let selectedCellsType: TimeOffCellType | undefined = undefined;
    for (let i = 0; i < this.selectedCells.length; i++) {
      const cell     = this.selectedCells[i];
      const cellType = timeOffModel.getCellType(cell);
      if (!selectedCellsType) {
        selectedCellsType = cellType;
      } else if (selectedCellsType !== cellType) {
        return undefined;
      }
    }
    return selectedCellsType;
  }

  get isMultiSelect(): boolean {
    return timeOffModel.isMultiSelect;
  }

  get currentInfoPanelModel(): any {
    if (this.selectedCellType) {
      return infoPanelTimeOffRequestViewModel;
    }

    return undefined;
  }

  get loadValue(): any {
    return this.state.loadValue;
  }

  get isLoading(): boolean {
    return this.state.isLoading || timeOffModel.isLoading;
  }

  reset() {
    if (!this.currentInfoPanelModel) {
      return;
    }

    this.currentInfoPanelModel.reset();
  }

  async load() {
    this.activate();
    await (this.state.isLoading = true);

    if (!this.currentInfoPanelModel) {
      this.state.isLoading = false;
      return;
    }

    if (this.selectedCellType === 'Employee' || this.selectedCellType === 'Job') {
      this.state.loadValue = this.getSelectedRowCompatibleTimeOffRequests(this.selectedCell!.row);
      if (!this.loadValue) {
        this.state.isLoading = false;
        return;
      }
    } else if (this.selectedCellType === 'ListViewCell') {
      const tor            = timeOffModel.timeOffRequests.find(tor => tor.id === this.selectedCell!.row.id);
      this.state.loadValue = tor ? [tor] : tor;
    } else {
      this.state.loadValue = this.selectedCell!.value?.timeOffRequests;
    }

    await this.currentInfoPanelModel.load(this.loadValue);
    this.state.isLoading = false;
  }

  getSelectedRowCompatibleTimeOffRequests(selectedRow: IRow): ITimeOffRequest[] | undefined {
    // returns the rows time off requests if they exist, and they are compatible.
    // i.e. if there are more than one tor, it has to be on the same day(s) as every other tor for the row.
    let timeOffRequests: ITimeOffRequest[] | undefined = undefined;
    for (const key of Object.keys(selectedRow.data)) {
      if (key.startsWith('date')) {
        const timeOffDateData = selectedRow.data[key];
        if (!timeOffDateData.timeOffRequests?.length) {
          continue;
        }
        const torIds                = timeOffRequests && new Set(timeOffRequests?.map((tor: ITimeOffRequest) => tor.id));
        const timeOffDateDataTorIds = new Set(timeOffDateData.timeOffRequests.map((tor: ITimeOffRequest) => tor.id));
        if (torIds && !areSetsEqual(torIds, timeOffDateDataTorIds)) {
          return undefined;
        }
        timeOffRequests = timeOffDateData.timeOffRequests;
      }
    }

    return timeOffRequests;
  }

  submit = async () => {
    if (this.selectedCell) {
      // restore focus to selected grid cell on save.
      this.selectedCell.setFocus();
    }
    return false;
  };
}

const timeOffInfoPanelModel = new TimeOffInfoPanelModel();
export default timeOffInfoPanelModel;
